import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  LinearProgress,
  Autocomplete,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Badge,
  Snackbar,
  Stack,
  useMediaQuery,
  useTheme,
  Menu,
} from '@mui/material';
import { DataGrid, itIT } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  CloudUpload as UploadIcon,
  AccountBalance as BankIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  PlayArrow as AnalyzeIcon,
  Description as InvoiceIcon,
  Savings as AccontoIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  AutoAwesome as AutoIcon,
  CheckCircleOutline as ConfirmAllIcon,
  Undo as UndoIcon,
  Schedule as ScheduleIcon,
  ErrorOutline as ErrorIcon,
  Info as InfoIcon,
  LocalPostOffice as PostalIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { paymentsApi, bankStatementsApi, customersApi, invoicesApi, postalSlipsApi } from '../../services/api';
import PayPalPaymentsTab from './PayPalPaymentsTab';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import PostalSlipsTab from './PostalSlipsTab';
import BankCustomerSearch from './BankCustomerSearch';
import { formatNuf } from '../../utils/formatNuf';
import { formatCustomerName } from '../../utils/formatCustomerName';
import ResponsiveHeader from '../../components/ResponsiveHeader';
import ResponsiveFilters from '../../components/ResponsiveFilters';
import MobileCardList from '../../components/MobileCardList';

const invalidateBankCaches = queryClient => {
  ['bank-statement-rows', 'bank-statements', 'bank-statements-all', 'payments', 'invoices', 'invoice', 'invoiceEditor', 'customerInvoices', 'customerBalance', 'customer-payments', 'customer-advances', 'customers'].forEach(key => queryClient.invalidateQueries({ queryKey: [key] }));
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

// Mappatura per visualizzazione etichette (supporta sia valori inglesi che italiani)
const PAYMENT_METHOD_LABELS = {
  // Valori inglesi (nuovi)
  cash: 'Contanti',
  bank_transfer: 'Bonifico',
  bank_draft: 'Assegno',
  rid: 'RID',
  credit_card: 'Carta',
  postal: 'Postale',
  paypal: 'PayPal',
  paypal_refund: 'Rimborso / storno PayPal',
  paypal_unassign: 'Annullamento associazione PayPal',
  // Valori italiani (legacy/manuali)
  contanti: 'Contanti',
  bollettino: 'Bollettino',
  bonifico: 'Bonifico',
  postagiro: 'Postagiro',
  rid_sepa: 'RID SEPA',
};

// Opzioni per il filtro metodo (valori effettivamente usati nel database)
const PAYMENT_METHOD_FILTER_OPTIONS = [
  { value: 'paypal', label: 'PayPal' },
  { value: 'paypal_refund', label: 'Rimborso / storno PayPal' },
  { value: 'paypal_unassign', label: 'Annullamento associazione PayPal' },
  { value: 'bank_transfer', label: 'Bonifico' },
  { value: 'postal', label: 'Postale' },
  { value: 'bollettino', label: 'Bollettino' },
  { value: 'contanti', label: 'Contanti' },
  { value: 'rid_sepa', label: 'RID SEPA' },
  { value: 'rid', label: 'RID' },
  { value: 'postagiro', label: 'Postagiro' },
];

const ASSIGNMENT_INVOICE_PAGE_SIZE = 200;

const fetchCustomerInvoicesForAssignment = async (customerId) => {
  const params = {
    customer_id: customerId,
    limit: ASSIGNMENT_INVOICE_PAGE_SIZE,
    sortBy: 'f_emission',
    sortOrder: 'desc',
  };

  const firstResponse = await invoicesApi.getAll({ ...params, page: 1 });
  const firstPage = firstResponse.data?.data || [];
  const totalPages = parseInt(firstResponse.data?.pagination?.totalPages, 10) || 1;

  if (totalPages <= 1) {
    return firstPage;
  }

  const remainingResponses = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) => (
      invoicesApi.getAll({ ...params, page: index + 2 })
    ))
  );

  return firstPage.concat(
    remainingResponses.flatMap(response => response.data?.data || [])
  );
};

// ============================================
// UTILITY FUNCTIONS PER RICONCILIAZIONE
// ============================================

/**
 * Restituisce colore e label basati sulla confidence
 */
const getConfidenceInfo = (confidence) => {
  if (confidence >= 85) return { color: 'success', label: 'Alto', bgColor: '#e8f5e9' };
  if (confidence >= 60) return { color: 'warning', label: 'Medio', bgColor: '#fff3e0' };
  if (confidence >= 40) return { color: 'primary', label: 'Basso', bgColor: '#e3f2fd' };
  return { color: 'default', label: 'Minimo', bgColor: '#f5f5f5' };
};

/**
 * Evidenzia le parti matchate nella causale con colori diversi per tipo
 * @param {string} causale - Testo originale della causale
 * @param {object} matchedFields - Oggetto con i campi matchati e posizioni
 * @returns {React.ReactNode} - Causale con evidenziazioni
 */
const highlightCausale = (causale, matchedFields) => {
  if (!causale || !matchedFields) return causale;
  
  // Raccogli tutte le regioni da evidenziare
  const highlights = [];
  
  if (matchedFields.invoiceNumber) {
    highlights.push({
      ...matchedFields.invoiceNumber,
      type: 'invoice',
      color: '#fff59d', // Giallo per fattura
      label: 'Numero fattura'
    });
  }
  if (matchedFields.customerCode) {
    highlights.push({
      ...matchedFields.customerCode,
      type: 'code',
      color: '#b3e5fc', // Azzurro per codice
      label: 'Codice cliente'
    });
  }
  if (matchedFields.customerName) {
    highlights.push({
      ...matchedFields.customerName,
      type: 'name',
      color: '#c8e6c9', // Verde per nome
      label: 'Nome cliente'
    });
  }
  if (matchedFields.fiscalCode) {
    highlights.push({ ...matchedFields.fiscalCode, color: '#d1c4e9', label: 'Codice fiscale letto' });
  }
  if (matchedFields.amount) {
    highlights.push({
      ...matchedFields.amount,
      type: 'amount',
      color: '#ffccbc', // Arancione per importo
      label: 'Importo'
    });
  }
  
  if (highlights.length === 0) return causale;
  
  // Ordina per posizione start
  highlights.sort((a, b) => a.start - b.start);
  
  // Costruisci il risultato con React fragments
  const result = [];
  let lastEnd = 0;
  
  highlights.forEach((h, idx) => {
    if (!Number.isInteger(h.start) || !Number.isInteger(h.end) || h.start < lastEnd || h.end > causale.length || h.end <= h.start || (h.text && causale.slice(h.start, h.end) !== h.text)) return;
    // Testo prima dell'highlight
    if (h.start > lastEnd) {
      result.push(<span key={`text-${idx}`}>{causale.substring(lastEnd, h.start)}</span>);
    }
    // Testo evidenziato con tooltip
    result.push(
      <Tooltip key={`hl-${idx}`} title={h.label} arrow>
        <span style={{ 
          backgroundColor: h.color, 
          padding: '1px 3px', 
          borderRadius: '3px',
          fontWeight: 600
        }}>
          {causale.substring(h.start, h.end)}
        </span>
      </Tooltip>
    );
    lastEnd = h.end;
  });
  
  // Testo dopo l'ultimo highlight
  if (lastEnd < causale.length) {
    result.push(<span key="text-end">{causale.substring(lastEnd)}</span>);
  }
  
  return <>{result}</>;
};

// ============================================
// TAB PANEL COMPONENT
// ============================================
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// ============================================
// TIPI DI PAGAMENTO
// ============================================
const MANUAL_PAYMENT_TYPES = [
  { value: 'bollettino', label: 'Bollettino' },
  { value: 'bonifico', label: 'Bonifico' },
  { value: 'postagiro', label: 'Postagiro' },
  { value: 'rid_sepa', label: 'RID SEPA' },
  { value: 'contanti', label: 'Contanti' },
  { value: 'compensazione', label: 'Compensazione' },
];

// ============================================
// COMPONENTE RIGA FATTURA PER PAGAMENTO MANUALE
// ============================================
const InvoicePaymentRow = ({ invoice, onSavePayment, onDeletePayment, isSaving, isDeleting, savedPayment, lastPaymentDate }) => {
  const [tipoPagamento, setTipoPagamento] = useState('');
  const [importo, setImporto] = useState('');
  const [dataPagamento, setDataPagamento] = useState(lastPaymentDate || new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  
  const insoluto = parseFloat(invoice.insoluto) || 0;
  const isCreditNote = invoice.isCreditNote || parseFloat(invoice.f_totale) < 0;
  // Per le note di credito l'insoluto è negativo, usiamo il valore assoluto per i calcoli
  const insolutoAbs = Math.abs(insoluto);
  const isSelected = tipoPagamento !== '';
  const savedStatus = savedPayment?.status;
  const savedPaymentId = savedPayment?.paymentId;
  
  // Warning per importi anomali
  const importoNum = parseFloat(importo) || 0;
  const isNegative = importoNum < 0;
  // Tolleranza per confronto floating point (0.01 = 1 centesimo)
  const isOverpay = importoNum > (insolutoAbs + 0.01);
  const showWarning = isSelected && (isNegative || isOverpay);

  // Gestione selezione tipo pagamento
  const handleTipoPagamentoChange = (e) => {
    const tipo = e.target.value;
    setTipoPagamento(tipo);
    if (tipo && !importo) {
      // Per le NC usiamo il valore assoluto dell'insoluto
      setImporto(insolutoAbs.toFixed(2));
    }
    if (tipo && lastPaymentDate) {
      setDataPagamento(lastPaymentDate);
    }
  };

  // Gestione salvataggio
  const handleSave = () => {
    if (!tipoPagamento || importoNum <= 0) return;
    
    onSavePayment({
      invoice_id: invoice.id,
      customer_id: invoice.customer_id,
      importo: importoNum,
      data_pagamento: dataPagamento,
      modalita: tipoPagamento,
      note: note || `${isCreditNote ? 'Rimborso' : 'Pagamento'} ${MANUAL_PAYMENT_TYPES.find(t => t.value === tipoPagamento)?.label || tipoPagamento}`
    });
  };

  // Determina colore riga in base allo stato salvato e tipo documento
  const getRowBgColor = () => {
    if (savedStatus === 'paid') return 'success.100';
    if (savedStatus === 'partial') return 'warning.100';
    if (isSelected) return 'action.selected';
    // Note di credito in azzurro chiaro
    if (isCreditNote) return 'info.50';
    return 'inherit';
  };

  return (
    <TableRow 
      hover={!savedStatus}
      sx={{ 
        bgcolor: getRowBgColor(),
        '&:hover': { bgcolor: savedStatus ? getRowBgColor() : 'action.hover' }
      }}
    >
      {/* Stato */}
      <TableCell>
        {savedStatus === 'paid' && <CheckIcon color="success" />}
        {savedStatus === 'partial' && <WarningIcon color="warning" />}
      </TableCell>

      {/* Numero Fattura */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight="bold">
            {formatNuf(invoice.sezionale, invoice.nuf)}
          </Typography>
          {isCreditNote && (
            <Chip 
              label="NC" 
              size="small" 
              color="info" 
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      </TableCell>

      {/* Data Fattura */}
      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        <Typography variant="body2" noWrap>
          {invoice.f_emission ? format(parseISO(invoice.f_emission), 'dd/MM/yyyy') : '-'}
        </Typography>
      </TableCell>

      {/* Cliente */}
      <TableCell>
        <Typography variant="body2" fontWeight="medium" noWrap>
          {invoice.customer?.ragione_sociale || 
           `${invoice.customer?.cognome || ''} ${invoice.customer?.nome || ''}`.trim() || 
           invoice.f_nome || invoice.f_cod_cli}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {invoice.customer?.cod_cli || invoice.f_cod_cli}
        </Typography>
      </TableCell>

      {/* Contatore */}
      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
        <Typography variant="body2" noWrap>
          {invoice.meter?.meter_number || invoice.meter?.nu_conta || '-'}
        </Typography>
      </TableCell>

      {/* Totale Fattura */}
      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        <Typography variant="body2" noWrap>
          {formatCurrency(invoice.f_totale)}
        </Typography>
      </TableCell>

      {/* Già Pagato */}
      <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
        <Typography variant="body2" color="success.main" noWrap>
          {formatCurrency(invoice.f_paga_imp || 0)}
        </Typography>
      </TableCell>

      {/* Insoluto / Credito */}
      <TableCell align="right">
        <Typography 
          variant="body2" 
          fontWeight="bold" 
          color={isCreditNote ? 'info.main' : 'error.main'} 
          noWrap
        >
          {isCreditNote ? formatCurrency(insolutoAbs) : formatCurrency(insoluto)}
        </Typography>
        {isCreditNote && (
          <Typography variant="caption" color="info.main">
            (credito)
          </Typography>
        )}
      </TableCell>

      {/* Tipo Pagamento */}
      <TableCell>
        <FormControl fullWidth size="small" disabled={!!savedStatus}>
          <Select
            value={tipoPagamento}
            onChange={handleTipoPagamentoChange}
            displayEmpty
            sx={{ bgcolor: isSelected && !savedStatus ? 'primary.50' : 'inherit' }}
          >
            <MenuItem value="">
              <em>Seleziona...</em>
            </MenuItem>
            {MANUAL_PAYMENT_TYPES.map(pt => (
              <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>

      {/* Importo */}
      <TableCell>
        <TextField
          type="number"
          value={importo}
          onChange={(e) => setImporto(e.target.value)}
          disabled={!isSelected || !!savedStatus}
          size="small"
          fullWidth
          inputProps={{ step: 0.01, min: 0 }}
          error={showWarning}
          sx={{ '& input': { textAlign: 'right' } }}
          InputProps={{
            startAdornment: <InputAdornment position="start">€</InputAdornment>
          }}
        />
        {showWarning && (
          <Typography variant="caption" color="warning.main">
            {isNegative ? 'Negativo!' : 'Maggiore insoluto!'}
          </Typography>
        )}
      </TableCell>

      {/* Data Pagamento */}
      <TableCell>
        <TextField
          type="date"
          value={dataPagamento}
          onChange={(e) => setDataPagamento(e.target.value)}
          disabled={!isSelected || !!savedStatus}
          size="small"
          fullWidth
        />
      </TableCell>

      {/* Note */}
      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
        <TextField
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={!isSelected || !!savedStatus}
          size="small"
          fullWidth
          placeholder="Note..."
        />
      </TableCell>

      {/* Azione Salva */}
      <TableCell>
        {!savedStatus && (
          <Button
            variant="contained"
            size="small"
            color="success"
            disabled={!isSelected || importoNum <= 0 || isSaving}
            onClick={handleSave}
            startIcon={<SaveIcon />}
          >
            Salva
          </Button>
        )}
        {savedStatus && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={savedStatus === 'paid' ? 'Saldato' : 'Parziale'} 
              size="small" 
              color={savedStatus === 'paid' ? 'success' : 'warning'}
            />
            <Tooltip title="Annulla pagamento">
              <IconButton
                size="small"
                color="error"
                onClick={() => onDeletePayment(savedPaymentId, invoice.id)}
                disabled={isDeleting}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};

// ============================================
// COMPONENTE TAB REGISTRAZIONE PAGAMENTI MANUALE
// ============================================
const ManualPaymentTab = ({ queryClient }) => {
  const currentYear = new Date().getFullYear();

  // Stato filtri
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterYear, setFilterYear] = useState(currentYear);
  const [filterMonth, setFilterMonth] = useState('');

  // Stato paginazione
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Stato pagamenti salvati nella sessione { invoiceId: { status: 'paid' | 'partial', paymentId: number } }
  const [savedPayments, setSavedPayments] = useState({});
  
  // Ultima data pagamento usata
  const [lastPaymentDate, setLastPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  // ID fattura in salvataggio
  const [savingInvoiceId, setSavingInvoiceId] = useState(null);
  
  // ID pagamento in eliminazione
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Debounce ricerca
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Query fatture aperte
  const invoicesQuery = useQuery({
    queryKey: ['open-invoices-manual', debouncedSearch, filterYear, filterMonth, page, rowsPerPage],
    queryFn: async () => {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterYear) params.anno = filterYear;
      if (filterMonth) params.mese = filterMonth;
      
      const res = await paymentsApi.getOpenInvoices(params);
      return res.data;
    }
  });

  // Mutation per salvare singolo pagamento
  const saveMutation = useMutation({
    mutationFn: async (paymentData) => {
      setSavingInvoiceId(paymentData.invoice_id);
      const res = await paymentsApi.create(paymentData);
      return { response: res.data, paymentData };
    },
    onSuccess: ({ response, paymentData }) => {
      // Determina se pagamento totale o parziale
      const invoice = invoicesQuery.data?.data?.find(inv => inv.id === paymentData.invoice_id);
      const insoluto = invoice ? parseFloat(invoice.insoluto) : 0;
      const isCreditNote = invoice?.isCreditNote || parseFloat(invoice?.f_totale) < 0;
      const insolutoAbs = Math.abs(insoluto);
      const importoPagato = parseFloat(paymentData.importo) || 0;
      
      // Tolleranza di 0.01 (1 centesimo) per confronto floating point
      // Per le NC l'insoluto è negativo, usiamo il valore assoluto
      const status = (importoPagato >= insolutoAbs - 0.01) ? 'paid' : 'partial';
      
      // Salva stato e ID del pagamento
      setSavedPayments(prev => ({
        ...prev,
        [paymentData.invoice_id]: {
          status,
          paymentId: response.data?.id || response.id
        }
      }));
      
      // Aggiorna ultima data pagamento usata
      setLastPaymentDate(paymentData.data_pagamento);
      
      setSnackbar({ 
        open: true, 
        message: `${isCreditNote ? 'Rimborso' : 'Pagamento'} di ${formatCurrency(importoPagato)} registrato con successo`, 
        severity: 'success' 
      });
      
      // Invalida cache pagamenti (ma NON open-invoices-manual per mantenere le fatture pagate visibili)
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      
      setSavingInvoiceId(null);
    },
    onError: (err) => {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Errore nella registrazione del pagamento', 
        severity: 'error' 
      });
      setSavingInvoiceId(null);
    }
  });

  // Mutation per eliminare pagamento
  const deleteMutation = useMutation({
    mutationFn: async ({ paymentId, invoiceId }) => {
      setDeletingPaymentId(paymentId);
      const res = await paymentsApi.delete(paymentId);
      return { response: res.data, invoiceId };
    },
    onSuccess: ({ response, invoiceId }) => {
      // Rimuovi dalla lista dei salvati
      setSavedPayments(prev => {
        const updated = { ...prev };
        delete updated[invoiceId];
        return updated;
      });
      
      setSnackbar({ 
        open: true, 
        message: 'Pagamento annullato con successo', 
        severity: 'info' 
      });
      
      // Invalida cache pagamenti e fatture aperte
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['open-invoices-manual'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      
      setDeletingPaymentId(null);
    },
    onError: (err) => {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Errore nell\'annullamento del pagamento', 
        severity: 'error' 
      });
      setDeletingPaymentId(null);
    }
  });

  // Handler salvataggio singolo pagamento
  const handleSavePayment = useCallback((paymentData) => {
    saveMutation.mutate(paymentData);
  }, [saveMutation]);

  // Handler eliminazione pagamento
  const handleDeletePayment = useCallback((paymentId, invoiceId) => {
    deleteMutation.mutate({ paymentId, invoiceId });
  }, [deleteMutation]);

  // Anni disponibili
  const years = useMemo(() => {
    const yearsArray = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
      yearsArray.push(y);
    }
    return yearsArray;
  }, [currentYear]);

  // Mesi
  const months = [
    { value: 1, label: 'Gennaio' },
    { value: 2, label: 'Febbraio' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Aprile' },
    { value: 5, label: 'Maggio' },
    { value: 6, label: 'Giugno' },
    { value: 7, label: 'Luglio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Settembre' },
    { value: 10, label: 'Ottobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Dicembre' },
  ];

  const invoices = invoicesQuery.data?.data || [];
  const totalCount = parseInt(invoicesQuery.data?.pagination?.total, 10) || 0;

  // Contatori salvati nella sessione
  const savedCount = Object.keys(savedPayments).length;
  const paidCount = Object.values(savedPayments).filter(s => s?.status === 'paid').length;
  const partialCount = Object.values(savedPayments).filter(s => s?.status === 'partial').length;

  return (
    <Box>
      {/* Filtri */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Ricerca */}
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cerca per n° fattura, matricola contatore, nome cliente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Filtro Anno */}
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Anno</InputLabel>
                <Select
                  value={filterYear}
                  label="Anno"
                  onChange={(e) => { setFilterYear(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  {years.map(y => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Filtro Mese */}
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Mese</InputLabel>
                <Select
                  value={filterMonth}
                  label="Mese"
                  onChange={(e) => { setFilterMonth(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  {months.map(m => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Refresh */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                {savedCount > 0 && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {paidCount > 0 && (
                      <Chip label={`${paidCount} saldati`} size="small" color="success" />
                    )}
                    {partialCount > 0 && (
                      <Chip label={`${partialCount} parziali`} size="small" color="warning" />
                    )}
                  </Box>
                )}
                <Tooltip title="Aggiorna lista">
                  <IconButton onClick={() => invoicesQuery.refetch()}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading */}
      {invoicesQuery.isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Errore */}
      {invoicesQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Errore nel caricamento delle fatture: {invoicesQuery.error?.message}
        </Alert>
      )}

      {/* Tabella Fatture */}
      <Card>
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'auto', width: '100%' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold', width: '3%' }}></TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>N° Fatt.</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '7%', display: { xs: 'none', md: 'table-cell' } }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '16%' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '7%', display: { xs: 'none', lg: 'table-cell' } }}>Contatore</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '7%', display: { xs: 'none', md: 'table-cell' } }} align="right">Totale</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '6%', display: { xs: 'none', lg: 'table-cell' } }} align="right">Pagato</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '7%' }} align="right">Insoluto</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Tipo Pag.</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '9%' }}>Importo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Data Pag.</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '8%', display: { xs: 'none', lg: 'table-cell' } }}>Note</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>Azione</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 && !invoicesQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Box sx={{ py: 4 }}>
                      <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography color="text.secondary">
                        Nessuna fattura da saldare trovata
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {invoices.map(invoice => (
                <InvoicePaymentRow
                  key={invoice.id}
                  invoice={invoice}
                  onSavePayment={handleSavePayment}
                  onDeletePayment={handleDeletePayment}
                  isSaving={savingInvoiceId === invoice.id}
                  isDeleting={deletingPaymentId === savedPayments[invoice.id]?.paymentId}
                  savedPayment={savedPayments[invoice.id]}
                  lastPaymentDate={lastPaymentDate}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Righe per pagina"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
        />
      </Card>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ============================================
// COMPONENTE PRINCIPALE
// ============================================
const PaymentList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);
  
  // Stati tab Registro
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMethod, setFilterMethod] = useState('all');
  
  // Dialog nuovo pagamento
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [newPayment, setNewPayment] = useState({
    importo: '',
    data_pagamento: new Date(),
    modalita: 'bank_transfer',
    riferimento: '',
    note: '',
  });

  // Stati Upload CSV/ZIP
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [csvFilename, setCsvFilename] = useState('');
  const [uploadNote, setUploadNote] = useState('');
  const [isZipFile, setIsZipFile] = useState(false);
  const [zipContent, setZipContent] = useState('');

  // Stati Riconciliazione
  const [selectedStatement, setSelectedStatement] = useState(null);

  // Paginazione Storico Estratti Conto
  const [stmtPage, setStmtPage] = useState(0);
  const [stmtRowsPerPage, setStmtRowsPerPage] = useState(25);

  // Reset pagina quando cambiano i filtri
  React.useEffect(() => {
    setPage(0);
  }, [searchQuery, filterYear, filterMethod]);

  // Query lista pagamenti
  const { data: paymentsData, isLoading: paymentsLoading } = useQuery({
    queryKey: ['payments', { page, pageSize, search: searchQuery, year: filterYear, method: filterMethod }],
    queryFn: () => paymentsApi.getAll({
      page: page + 1,
      limit: pageSize,
      search: searchQuery,
      year: filterYear,
      payment_method: filterMethod !== 'all' ? filterMethod : undefined,
    }).then(res => res.data),
    enabled: tabValue === 0,
  });

  // Query estratti conto (paginata per Storico)
  const { data: statementsData, isLoading: statementsLoading } = useQuery({
    queryKey: ['bank-statements', stmtPage, stmtRowsPerPage],
    queryFn: () => bankStatementsApi.getAll({ page: stmtPage + 1, limit: stmtRowsPerPage }).then(res => res.data),
    enabled: tabValue === 2,
  });

  // Query estratti conto TUTTI (per Riconciliazione)
  const { data: allStatementsData } = useQuery({
    queryKey: ['bank-statements-all'],
    queryFn: () => bankStatementsApi.getAll({ limit: 9999 }).then(res => res.data),
    enabled: tabValue === 1,
  });

  // Mutations
  const createPaymentMutation = useMutation({
    mutationFn: (data) => paymentsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      handleCloseDialog();
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (data) => bankStatementsApi.upload(data),
    onSuccess: (res, data) => {
      queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
      queryClient.invalidateQueries({ queryKey: ['bank-statements-all'] });
      invalidateBankCaches(queryClient);
      setUploadDialogOpen(false);
      setCsvContent('');
      setCsvFilename('');
      setUploadNote('');
      setIsZipFile(false);
      setZipContent('');
      
      // Mostra info sui duplicati se presenti
      const duplicatiEsclusi = res.data?.data?.duplicati_esclusi || 0;
      if (duplicatiEsclusi > 0) {
        console.log(`⚠️ ${duplicatiEsclusi} duplicati esclusi dall'upload`);
        const dettaglio = res.data?.data?.duplicati_dettaglio || [];
        if (dettaglio.length > 0) {
          console.log('Dettaglio duplicati:', dettaglio);
        }
      }
      
      // Vai alla tab riconciliazione
      setTabValue(1);
      setSelectedStatement({ ...res.data.data, filename: data.filename });
    },
  });

  const deleteStatementMutation = useMutation({
    mutationFn: (id) => bankStatementsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
      queryClient.invalidateQueries({ queryKey: ['bank-statements-all'] });
      invalidateBankCaches(queryClient);
      setSelectedStatement(null);
    },
  });

  // Mutation per eliminare pagamento
  const deletePaymentMutation = useMutation({
    mutationFn: (id) => paymentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  // Handlers
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCustomer(null);
    setSelectedInvoice(null);
    setInvoiceOptions([]);
    setNewPayment({
      importo: '',
      data_pagamento: new Date(),
      modalita: 'bank_transfer',
      riferimento: '',
      note: '',
    });
  };



  const handleCustomerSelect = async (customer) => {
    setSelectedCustomer(customer);
    setSelectedInvoice(null);
    if (customer) {
      try {
        const res = await invoicesApi.getAll({ 
          customer_id: customer.id, 
          stato: 'emessa,parziale,scaduta',
          limit: 50 
        });
        const invoices = (res.data?.data || []).map(inv => ({
          ...inv,
          saldo_residuo: parseFloat(inv.f_totale) - parseFloat(inv.f_paga_imp || 0)
        })).filter(inv => inv.saldo_residuo > 0);
        setInvoiceOptions(invoices);
      } catch (err) {
        console.error('Errore caricamento fatture:', err);
      }
    }
  };

  const handleInvoiceSelect = (invoice) => {
    setSelectedInvoice(invoice);
    if (invoice) {
      setNewPayment(prev => ({
        ...prev,
        importo: invoice.saldo_residuo.toFixed(2)
      }));
    }
  };

  const handleCreatePayment = () => {
    if (!selectedInvoice && !selectedCustomer) return;
    
    createPaymentMutation.mutate({
      invoice_id: selectedInvoice?.id,
      customer_id: selectedCustomer?.id,
      importo: parseFloat(newPayment.importo),
      data_pagamento: newPayment.data_pagamento?.toISOString().split('T')[0],
      modalita: newPayment.modalita,
      riferimento: newPayment.riferimento,
      note: newPayment.note,
    });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCsvFilename(file.name);
      const isZip = file.name.toLowerCase().endsWith('.zip');
      setIsZipFile(isZip);

      if (isZip) {
        setCsvContent('');
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = btoa(
            new Uint8Array(e.target.result).reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          setZipContent(base64);
        };
        reader.readAsArrayBuffer(file);
      } else {
        setZipContent('');
        const reader = new FileReader();
        reader.onload = (e) => {
          setCsvContent(e.target.result);
        };
        reader.readAsText(file, 'UTF-8');
      }
    }
  };

  const handleUploadSubmit = () => {
    if (!csvContent && !zipContent) return;
    const payload = {
      filename: csvFilename,
      note: uploadNote,
    };
    if (isZipFile && zipContent) {
      payload.zipContent = zipContent;
    } else {
      payload.content = csvContent;
    }
    uploadMutation.mutate(payload);
  };

  // Colonne DataGrid pagamenti
  const columns = [
    {
      field: 'data_pagamento',
      headerName: 'Data Pagamento',
      width: 130,
      valueFormatter: (params) => params.value ? format(new Date(params.value), 'dd/MM/yyyy') : '',
    },
    {
      field: 'invoice',
      headerName: 'Fattura',
      width: 100,
      renderCell: (params) => params.row.invoice ? (
        <Chip
          label={formatNuf(params.row.invoice.sezionale, params.row.invoice.nuf)}
          size="small"
          color="primary"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/crm/fatture/${params.row.invoice.id}`);
          }}
        />
      ) : <Chip label="Acconto" size="small" color="secondary" variant="outlined" />,
    },
    {
      field: 'customer',
      headerName: 'Cliente',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => {
        const cust = params.row.customer || params.row.invoice?.customer;
        return formatCustomerName(cust, '');
      },
    },
    {
      field: 'modalita',
      headerName: 'Metodo',
      width: 230,
      renderCell: (params) => (
        <Chip 
          label={(params.row.paypal?.environment === 'sandbox' ? 'Sandbox · prova — ' : '') + (PAYMENT_METHOD_LABELS[params.value] || params.value)}
          size="small" 
          variant="outlined" 
        />
      ),
    },
    {
      field: 'riferimento',
      headerName: 'Riferimento',
      width: 150,
    },
    {
      field: 'importo',
      headerName: 'Importo',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography fontWeight="medium" color="success.main">
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Elimina pagamento">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Sei sicuro di voler eliminare questo pagamento?')) {
                deletePaymentMutation.mutate(params.row.id);
              }
            }}
            disabled={deletePaymentMutation.isPending || String(params.row.modalita).startsWith('paypal')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const payments = paymentsData?.payments || [];
  const totalPayments = paymentsData?.total || 0;
  const statements = statementsData?.data || [];
  const allStatements = allStatementsData?.data || [];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
  const activeFilterCount = (filterYear !== currentYear ? 1 : 0) + (filterMethod !== 'all' ? 1 : 0);

  const handleResetRegisterFilters = () => {
    setFilterYear(currentYear);
    setFilterMethod('all');
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 0 } }}>
      <ResponsiveHeader
        title="Pagamenti"
        subtitle="Gestione pagamenti e riconciliazione bancaria"
        primaryAction={{
          label: isMobile ? 'Nuovo' : 'Nuovo Pagamento',
          icon: <AddIcon />,
          onClick: () => setDialogOpen(true),
        }}
        secondaryActions={[
          {
            label: isMobile ? 'Carica estratto' : 'Carica Estratto Conto',
            icon: <UploadIcon />,
            onClick: () => setUploadDialogOpen(true),
          },
        ]}
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab 
            label={isMobile ? 'Pagamenti' : 'Registro Pagamenti'} 
            icon={<ReceiptIcon />} 
            iconPosition="start"
          />
          <Tab 
            label={
              <Badge 
                badgeContent={statements.filter(s => s.stato === 'pending' || s.stato === 'in_progress').length} 
                color="warning"
              >
                Riconciliazione
              </Badge>
            } 
            icon={<BankIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Storico Estratti" 
            icon={<DownloadIcon />} 
            iconPosition="start"
          />
          <Tab 
            label={isMobile ? 'Bollettini' : 'Bollettini Postali'} 
            icon={<PostalIcon />} 
            iconPosition="start"
          />
          <Tab 
            label={isMobile ? 'Manuale' : 'Registrazione Manuale'} 
            icon={<EditIcon />} 
            iconPosition="start"
          />
          <Tab label="PayPal" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={5}><PayPalPaymentsTab /></TabPanel>
      {/* TAB 0: Registro Pagamenti */}
      <TabPanel value={tabValue} index={0}>
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
            <ResponsiveFilters
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Cerca per cliente, fattura..."
              activeFilterCount={activeFilterCount}
              onClearFilters={handleResetRegisterFilters}
            >
              <FormControl sx={{ minWidth: { xs: '100%', sm: 100 } }} size="small">
                <InputLabel>Anno</InputLabel>
                <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} label="Anno">
                  {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: { xs: '100%', sm: 130 } }} size="small">
                <InputLabel>Metodo</InputLabel>
                <Select value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)} label="Metodo">
                  <MenuItem value="all">Tutti</MenuItem>
                  {PAYMENT_METHOD_FILTER_OPTIONS.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </ResponsiveFilters>
          </CardContent>
        </Card>

        {isMobile ? (
          <MobileCardList
            rows={payments}
            loading={paymentsLoading}
            emptyMessage="Nessun pagamento trovato"
            pagination={{
              page,
              pageSize,
              total: totalPayments,
              onPageChange: setPage,
            }}
            renderCard={(payment) => {
              const customer = payment.customer || payment.invoice?.customer;
              const customerName = formatCustomerName(customer, '');

              return (
                <Card variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {customerName || 'Cliente non disponibile'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            {payment.data_pagamento ? format(new Date(payment.data_pagamento), 'dd/MM/yyyy') : '-'}
                          </Typography>
                          {payment.invoice ? (
                            <Chip
                              label={formatNuf(payment.invoice.sezionale, payment.invoice.nuf)}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                              onClick={() => navigate(`/crm/fatture/${payment.invoice.id}`)}
                            />
                          ) : (
                            <Chip label="Acconto" size="small" color="secondary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                          )}
                          {payment.modalita && (
                            <Chip
                              label={(payment.paypal?.environment === 'sandbox' ? 'Sandbox - prova: ' : '') + (PAYMENT_METHOD_LABELS[payment.modalita] || payment.modalita)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                        {payment.riferimento && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }} noWrap>
                            Rif: {payment.riferimento}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          {formatCurrency(payment.importo)}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={String(payment.modalita).startsWith('paypal')}
                          onClick={() => {
                            if (window.confirm('Eliminare questo pagamento?')) {
                              deletePaymentMutation.mutate(payment.id);
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            }}
          />
        ) : (
        <Card>
          <DataGrid
            rows={payments}
            columns={columns}
            loading={paymentsLoading}
            paginationMode="server"
            rowCount={totalPayments}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => { setPage(model.page); setPageSize(model.pageSize); }}
            pageSizeOptions={[10, 25, 50, 100]}
            autoHeight
            disableRowSelectionOnClick
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            sx={{ border: 'none' }}
          />
        </Card>
        )}
      </TabPanel>

      {/* TAB 1: Riconciliazione */}
      <TabPanel value={tabValue} index={1}>
        <ReconciliationTab 
          statements={allStatements}
          selectedStatement={selectedStatement}
          setSelectedStatement={setSelectedStatement}
          queryClient={queryClient}
        />
      </TabPanel>

      {/* TAB 2: Storico */}
      <TabPanel value={tabValue} index={2}>
        <StatementsHistoryTab 
          statements={statements}
          deleteStatementMutation={deleteStatementMutation}
          total={statementsData?.total || 0}
          page={stmtPage}
          rowsPerPage={stmtRowsPerPage}
          onPageChange={(e, newPage) => setStmtPage(newPage)}
          onRowsPerPageChange={(e) => { setStmtRowsPerPage(parseInt(e.target.value, 10)); setStmtPage(0); }}
        />
      </TabPanel>

      {/* TAB 3: Bollettini Postali */}
      <TabPanel value={tabValue} index={3}>
        <PostalSlipsTab />
      </TabPanel>

      {/* TAB 4: Registrazione Pagamenti Manuale */}
      <TabPanel value={tabValue} index={4}>
        <ManualPaymentTab queryClient={queryClient} />
      </TabPanel>

      {/* Dialog Nuovo Pagamento */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Nuovo Pagamento</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Ricerca Cliente */}
            <Grid item xs={12}>
              <BankCustomerSearch value={selectedCustomer} onChange={handleCustomerSelect} active={dialogOpen} label="Cerca Cliente" sx={{ mb: 2 }} />
            </Grid>

            {/* Selezione Fattura */}
            {selectedCustomer && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Fattura da pagare</InputLabel>
                  <Select
                    value={selectedInvoice?.id || ''}
                    onChange={(e) => {
                      const inv = invoiceOptions.find(i => i.id === e.target.value);
                      handleInvoiceSelect(inv);
                    }}
                    label="Fattura da pagare"
                  >
                    <MenuItem value="">
                      <em>Acconto generico (senza fattura)</em>
                    </MenuItem>
                    {invoiceOptions.map(inv => (
                      <MenuItem key={inv.id} value={inv.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>Fatt. {formatNuf(inv.sezionale, inv.nuf)} del {format(new Date(inv.f_emission), 'dd/MM/yyyy')}</span>
                          <Typography color="error.main" fontWeight="medium">
                            {formatCurrency(inv.saldo_residuo)}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                label="Importo"
                type="number"
                fullWidth
                value={newPayment.importo}
                onChange={(e) => setNewPayment(prev => ({ ...prev, importo: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Data Pagamento"
                value={newPayment.data_pagamento}
                onChange={(date) => setNewPayment(prev => ({ ...prev, data_pagamento: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Metodo</InputLabel>
                <Select
                  value={newPayment.modalita}
                  onChange={(e) => setNewPayment(prev => ({ ...prev, modalita: e.target.value }))}
                  label="Metodo"
                >
                  {Object.entries(PAYMENT_METHOD_LABELS).filter(([value]) => !/^paypal(?:_|$)/i.test(value)).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Riferimento (CRO, n. assegno...)"
                fullWidth
                value={newPayment.riferimento}
                onChange={(e) => setNewPayment(prev => ({ ...prev, riferimento: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Note"
                fullWidth
                multiline
                rows={2}
                value={newPayment.note}
                onChange={(e) => setNewPayment(prev => ({ ...prev, note: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={handleCreatePayment}
            disabled={!newPayment.importo || (!selectedInvoice && !selectedCustomer) || createPaymentMutation.isPending}
          >
            {createPaymentMutation.isPending ? <CircularProgress size={20} /> : 'Registra'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Upload CSV */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Carica Estratto Conto</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Carica il file CSV (o ZIP contenente il CSV) dell'estratto conto bancario. Il sistema rileverà automaticamente 
            i movimenti in entrata e proporrà le corrispondenze con le fatture.
          </Alert>
          
          <Box 
            sx={{ 
              border: '2px dashed', 
              borderColor: 'primary.main', 
              borderRadius: 2, 
              p: 3, 
              textAlign: 'center',
              mb: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' }
            }}
            onClick={() => document.getElementById('csv-upload').click()}
          >
            <input
              type="file"
              id="csv-upload"
              accept=".csv,.txt,.zip"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography>
              {csvFilename || 'Clicca o trascina il file CSV o ZIP qui'}
            </Typography>
            {csvContent && (
              <Typography variant="caption" color="success.main">
                File caricato: {csvContent.split('\n').length} righe
              </Typography>
            )}
            {isZipFile && zipContent && (
              <Typography variant="caption" color="success.main">
                File ZIP caricato — il CSV verrà estratto automaticamente
              </Typography>
            )}
          </Box>

          <TextField
            label="Note (opzionale)"
            fullWidth
            multiline
            rows={2}
            value={uploadNote}
            onChange={(e) => setUploadNote(e.target.value)}
            placeholder="Es: Estratto conto Novembre 2024"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={handleUploadSubmit}
            disabled={(!csvContent && !zipContent) || uploadMutation.isPending}
            startIcon={uploadMutation.isPending ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            Carica e Analizza
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================
// COMPONENTE RICONCILIAZIONE (MIGLIORATO)
// ============================================
export function ReconciliationTab({ statements, selectedStatement, setSelectedStatement, queryClient }) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({ status: 'all', date_from: '', date_to: '', amount_exact: '', amount_min: '', amount_max: '', confidence_min: '' });
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [sortBy, setSortBy] = useState('data_operazione');
  const [sortDir, setSortDir] = useState('desc');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [preview, setPreview] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [batchReport, setBatchReport] = useState(null);
  const sourceId = selectedStatement?.id;
  useEffect(() => { const timer = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(timer); }, [search]);
  useEffect(() => { setPage(0); setSelectedRows(new Set()); setExpandedRow(null); }, [filters, debouncedSearch, sourceId, limit, sortBy, sortDir]);
  useEffect(() => {
    setSearch(''); setDebouncedSearch('');
    setFilters({ status: 'all', date_from: '', date_to: '', amount_exact: '', amount_min: '', amount_max: '', confidence_min: '' });
  }, [sourceId]);
  const requestFilters = useMemo(() => ({ ...filters, search: debouncedSearch, ...(sourceId ? { statement_id: sourceId } : {}) }), [filters, debouncedSearch, sourceId]);
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['bank-statement-rows', 'movements', requestFilters, page, limit, sortBy, sortDir],
    queryFn: () => bankStatementsApi.getMovements({ ...requestFilters, page: page + 1, limit, sort: sortBy, direction: sortDir }).then(res => res.data.data),
  });
  const rows = data?.rows || [];
  const stats = data?.summary || {};
  useEffect(() => {
    if (data && page > Math.max(0, Math.ceil(data.pagination.total / limit) - 1)) setPage(Math.max(0, Math.ceil(data.pagination.total / limit) - 1));
  }, [data, page, limit]);
  const busy = Boolean(progress) || preparing;
  const notifyError = err => setSnackbar({ open: true, severity: 'error', message: err.response?.data?.message || err.message });
  const rowMutation = useMutation({
    mutationFn: ({ action, row }) => action === 'confirm' ? bankStatementsApi.confirmMovement(row.id) : bankStatementsApi.ignoreMovement(row.id),
    onSuccess: () => { invalidateBankCaches(queryClient); setSelectedRows(new Set()); }, onError: notifyError,
  });
  const changeFilter = key => event => setFilters(old => ({ ...old, [key]: event.target.value }));
  const handleSortColumn = key => { setSortBy(key); setSortDir(sortBy === key && sortDir === 'asc' ? 'desc' : 'asc'); };
  const selectable = rows.filter(row => !['confirmed', 'ignored'].includes(row.match_status));
  const toggleRowSelection = id => setSelectedRows(old => { const next = new Set(old); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const prepareBatch = async (action, selected = false) => {
    setPreparing(true);
    try {
      const mode = selected ? 'selected' : 'filtered';
      const result = (await bankStatementsApi.previewMovements({ action, mode, filters: requestFilters, ...(selected ? { rowIds: [...selectedRows] } : {}) })).data.data;
      if (!result.count) setSnackbar({ open: true, severity: 'info', message: 'Nessun movimento elaborabile per questa azione.' });
      else setPreview({ ...result, action, mode });
    } catch (err) { notifyError(err); } finally { setPreparing(false); }
  };
  const runBatch = async () => {
    const target = preview; setPreview(null); setBatchReport(null);
    let processed = 0, skipped = 0, completed = 0;
    const errors = [];
    setProgress({ completed: 0, total: target.count });
    try {
      for (let i = 0; i < target.rowIds.length; i += 25) {
        const result = (await bankStatementsApi.processMovements({ action: target.action, mode: target.mode, rowIds: target.rowIds.slice(i, i + 25) })).data.data;
        for (const item of result.results) {
          if (item.status === 'processed') processed++;
          else if (item.status === 'skipped') skipped++;
          else errors.push(item);
        }
        completed += result.results.length;
        setProgress({ completed, total: target.count });
      }
    } catch (err) { errors.push({ message: err.response?.data?.message || 'Connessione interrotta: aggiornare la lista prima di riprovare.' }); }
    finally {
      setProgress(null); setSelectedRows(new Set()); invalidateBankCaches(queryClient);
      setBatchReport({ processed, skipped, remaining: target.count - completed, errors });
    }
  };
  const sourceOptions = sourceId && !statements.some(s => s.id === sourceId) ? [selectedStatement, ...statements] : statements;
  return (
    <Card><CardContent>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">{sourceId ? 'Movimenti dell’estratto' : 'Tutti i movimenti'}</Typography>
          <Typography variant="body2" color="text.secondary">{stats.total || 0} movimenti • {formatCurrency(stats.amount)} • {stats.confirmed || 0} confermati • {(stats.pending || 0) + (stats.suggested || 0)} da elaborare</Typography>
        </Box>
        {sourceId && <Button variant="outlined" disabled={busy} onClick={() => setSelectedStatement(null)}>Termina e mostra tutti i movimenti</Button>}
      </Stack>
      {selectedStatement?.duplicati_esclusi > 0 && <Alert severity="info" sx={{ mb: 2 }}>
        {selectedStatement.duplicati_esclusi} duplicati esclusi. Qui sono mostrati soltanto i nuovi movimenti.
        <details><summary>Dettaglio duplicati</summary>{selectedStatement.duplicati_dettaglio?.map((item, i) => <Typography key={i} variant="body2">{item.data} • {formatCurrency(item.importo)} • {item.causale} • già presente in {item.estratto_precedente}</Typography>)}</details>
      </Alert>}
      <Box component="fieldset" disabled={busy} sx={{ border: 0, p: 0, m: 0 }}>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={12} md={7}><TextField fullWidth size="small" label="Cerca causale, cliente, codice fiscale, fattura o importo" value={search} onChange={e => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} /></Grid>
          <Grid item xs={12} md={5}><Autocomplete size="small" options={sourceOptions} value={selectedStatement} isOptionEqualToValue={(a, b) => a.id === b.id} getOptionLabel={option => option.filename || `Estratto ${option.id}`} onChange={(_, value) => setSelectedStatement(value)} renderInput={params => <TextField {...params} label="Estratto (tutti se vuoto)" />} /></Grid>
          <Grid item xs={6} md={2}><TextField fullWidth size="small" type="date" label="Data da" InputLabelProps={{ shrink: true }} value={filters.date_from} onChange={changeFilter('date_from')} /></Grid>
          <Grid item xs={6} md={2}><TextField fullWidth size="small" type="date" label="Data a" InputLabelProps={{ shrink: true }} value={filters.date_to} onChange={changeFilter('date_to')} /></Grid>
          {['amount_exact', 'amount_min', 'amount_max'].map((key, i) => <Grid key={key} item xs={4} md={2}><TextField fullWidth size="small" label={['Importo esatto €', 'Min €', 'Max €'][i]} value={filters[key]} inputProps={{ inputMode: 'decimal' }} onChange={changeFilter(key)} /></Grid>)}
          <Grid item xs={6} md={2}><TextField fullWidth size="small" label="Confidenza min %" type="number" inputProps={{ min: 0, max: 100 }} value={filters.confidence_min} onChange={changeFilter('confidence_min')} /></Grid>
          <Grid item xs={6} md={3}><TextField select fullWidth size="small" label="Stato" value={filters.status} onChange={changeFilter('status')}>
            {[['all', 'Tutti'], ['pending', 'Da elaborare'], ['suggested', 'Match proposti / parziali'], ['confirmed', 'Confermati'], ['ignored', 'Ignorati']].map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField></Grid>
          <Grid item xs={6} md={2}><Button onClick={() => { setSearch(''); setFilters({ status: 'all', date_from: '', date_to: '', amount_exact: '', amount_min: '', amount_max: '', confidence_min: '' }); }}>Azzera filtri</Button></Grid>
        </Grid>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <Button variant="outlined" startIcon={<AutoIcon />} onClick={() => prepareBatch('analyze')} disabled={busy || Boolean(error)}>Analizza tutto</Button>
          <Button variant="outlined" color="success" startIcon={<ConfirmAllIcon />} onClick={() => prepareBatch('confirm')} disabled={busy || Boolean(error)}>Conferma match ≥85%</Button>
          {selectedRows.size > 0 && <Button variant="contained" color="success" onClick={() => prepareBatch('confirm', true)} disabled={busy}>Conferma {selectedRows.size} selezionati</Button>}
          <Button variant="outlined" onClick={() => prepareBatch('ignore')} disabled={busy || Boolean(error)}>Ignora senza match</Button>
        </Stack>
      </Box>
      {progress && <Box sx={{ mb: 2 }}><Typography>{progress.completed}/{progress.total} movimenti elaborati</Typography><LinearProgress variant="determinate" value={100 * progress.completed / progress.total} /></Box>}
      {batchReport && <Alert severity={batchReport.errors.length || batchReport.remaining ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setBatchReport(null)}>
        {batchReport.processed} elaborati, {batchReport.skipped} saltati, {batchReport.errors.length} errori{batchReport.remaining > 0 ? `, ${batchReport.remaining} da verificare` : ''}.
        {batchReport.errors.length > 0 && <details><summary>Dettaglio errori</summary>{batchReport.errors.map((item, i) => <Typography key={i} variant="body2">{item.rowId ? `Movimento ${item.rowId}: ` : ''}{item.message}</Typography>)}</details>}
      </Alert>}
      {error && <Alert severity="error">{error.response?.data?.message || 'Impossibile caricare i movimenti'}</Alert>}
      {(isFetching || preparing) && <LinearProgress />}
      {!isLoading && !error && <>
        <Box component="fieldset" disabled={busy || rowMutation.isPending} sx={{ border: 0, p: 0, m: 0 }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)', opacity: busy ? 0.65 : 1 }}>
          <Table size="small" stickyHeader><TableHead><TableRow>
            <TableCell padding="checkbox"><Checkbox size="small" checked={selectable.length > 0 && selectable.every(row => selectedRows.has(row.id))} indeterminate={selectedRows.size > 0 && !selectable.every(row => selectedRows.has(row.id))} onChange={() => setSelectedRows(selectedRows.size ? new Set() : new Set(selectable.map(row => row.id)))} inputProps={{ 'aria-label': 'Seleziona i movimenti elaborabili di questa pagina' }} /></TableCell>
            <TableCell />
            {['data_operazione', 'causale'].map(key => <TableCell key={key}><TableSortLabel active={sortBy === key} direction={sortBy === key ? sortDir : 'asc'} onClick={() => handleSortColumn(key)}>{key === 'causale' ? 'Causale / Estratto' : 'Data'}</TableSortLabel></TableCell>)}
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Abbinamenti / Proposte</TableCell>
            <TableCell align="right"><TableSortLabel active={sortBy === 'importo'} direction={sortBy === 'importo' ? sortDir : 'asc'} onClick={() => handleSortColumn('importo')}>Importo</TableSortLabel></TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><TableSortLabel active={sortBy === 'match_confidence'} direction={sortBy === 'match_confidence' ? sortDir : 'asc'} onClick={() => handleSortColumn('match_confidence')}>Confidenza</TableSortLabel></TableCell>
            <TableCell><TableSortLabel active={sortBy === 'match_status'} direction={sortBy === 'match_status' ? sortDir : 'asc'} onClick={() => handleSortColumn('match_status')}>Stato</TableSortLabel></TableCell><TableCell>Azioni</TableCell>
          </TableRow></TableHead><TableBody>
            {rows.map(row => <ReconciliationRow key={row.id} row={row} statement={row.bankStatement || { id: row.bank_statement_id }} expanded={expandedRow === row.id} selected={selectedRows.has(row.id)} onSelect={() => toggleRowSelection(row.id)} onExpand={() => setExpandedRow(expandedRow === row.id ? null : row.id)} onConfirm={() => rowMutation.mutate({ action: 'confirm', row })} onIgnore={() => rowMutation.mutate({ action: 'ignore', row })} queryClient={queryClient} />)}
            {!rows.length && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>Nessun movimento trovato con i filtri selezionati</TableCell></TableRow>}
          </TableBody></Table>
        </TableContainer>
        <TablePagination component="div" count={data?.pagination.total || 0} page={page} rowsPerPage={limit} rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage="Movimenti per pagina" labelDisplayedRows={({ from, to, count }) => `${from}–${to} di ${count}`} onPageChange={(_, value) => { setPage(value); setSelectedRows(new Set()); }} onRowsPerPageChange={e => setLimit(Number(e.target.value))} />
        </Box>
      </>}
      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)}><DialogTitle>Conferma operazione</DialogTitle><DialogContent>
        <Typography>{preview?.action === 'analyze' ? 'Analizzare' : preview?.action === 'ignore' ? 'Ignorare' : 'Confermare'} {preview?.count} movimenti elaborabili?</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>{preview?.mode === 'selected' ? 'Saranno elaborati i movimenti selezionati.' : 'Saranno elaborati tutti i risultati filtrati, anche nelle altre pagine.'}</Typography>
      </DialogContent><DialogActions><Button onClick={() => setPreview(null)}>Annulla</Button><Button variant="contained" onClick={runBatch}>Conferma</Button></DialogActions></Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(old => ({ ...old, open: false }))}><Alert severity={snackbar.severity}>{snackbar.message}</Alert></Snackbar>
    </CardContent></Card>
  );
}


// ============================================
// SINGOLA RIGA RICONCILIAZIONE (MIGLIORATA)
// ============================================
function MovementAssignments({ row, bestSuggestion }) {
  const assignments = (row.assignedPayments || []).filter(a => a.stato !== 'cancelled');
  if (assignments.length) return <Stack spacing={0.5}>{assignments.map(a => <Box key={a.id}>
    <Typography variant="caption" color={a.stato === 'confirmed' ? 'success.main' : 'text.secondary'}>{a.stato === 'confirmed' ? 'Abbinamento confermato' : 'Assegnazione da confermare'}</Typography>
    <Typography variant="body2">{a.customer ? formatCustomerName(a.customer) : row.customer ? formatCustomerName(row.customer) : `Cliente ${a.customer_id}`}</Typography>
    <Typography variant="caption">{a.invoice ? `Fatt. ${formatNuf(a.invoice.sezionale, a.invoice.nuf)}` : 'Acconto'} • {formatCurrency(a.importo)}</Typography>
  </Box>)}</Stack>;
  if (bestSuggestion) return <Box>{bestSuggestion.matchingVersion !== 'bank-v2' && <Typography variant="caption" color="warning.main" display="block">Rianalizza per aggiornare la verifica automatica</Typography>}<Typography variant="caption" color="info.main">Proposta di abbinamento</Typography><Typography variant="body2">{bestSuggestion.customerName}</Typography><Typography variant="caption">{bestSuggestion.invoiceNum ? `Fatt. ${bestSuggestion.invoiceNum}` : 'Acconto'}</Typography>{bestSuggestion.customerFiscalCode && <Typography variant="caption" display="block">CF cliente: {bestSuggestion.customerFiscalCode}</Typography>}{bestSuggestion.warnings?.map(warning => <Typography key={warning} variant="caption" color="warning.main" display="block">{warning}</Typography>)}{!bestSuggestion.warnings?.length && (bestSuggestion.customerMismatch || bestSuggestion.isGiaPagata) && <Typography variant="caption" color="error" display="block">{bestSuggestion.customerMismatch ? 'Cliente non corrispondente' : 'Fattura già pagata'}</Typography>}</Box>;
  return row.customer ? <Typography variant="body2">Cliente identificato: {formatCustomerName(row.customer)}</Typography> : <Typography variant="caption" color="text.secondary">Nessun abbinamento</Typography>;
}

function ReconciliationRow({ row, statement, expanded, selected, onSelect, onExpand, onConfirm, onIgnore, queryClient }) {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  
  // Parse suggestions JSON se presente
  const suggestions = useMemo(() => {
    if (row.suggestions_json) {
      try {
        return JSON.parse(row.suggestions_json);
      } catch { return []; }
    }
    return [];
  }, [row.suggestions_json]);
  
  const bestSuggestion = suggestions[0] || null;
  const hasMultipleSuggestions = suggestions.length > 1;

  const getStatusChip = (status) => {
    const config = {
      pending: { label: 'Da elaborare', color: 'warning', icon: <ScheduleIcon fontSize="small" /> },
      suggested: { label: 'Match trovato', color: 'info', icon: <InfoIcon fontSize="small" /> },
      partial: { label: 'Parziale', color: 'warning', icon: <WarningIcon fontSize="small" /> },
      confirmed: { label: 'Confermato', color: 'success', icon: <CheckIcon fontSize="small" /> },
      ignored: { label: 'Ignorato', color: 'default', icon: <CancelIcon fontSize="small" /> },
    };
    // null o undefined è considerato 'pending'
    const effectiveStatus = status || 'pending';
    const c = config[effectiveStatus] || config.pending;
    return <Chip label={c.label} color={c.color} size="small" icon={c.icon} />;
  };

  const confidenceInfo = row.match_confidence ? getConfidenceInfo(row.match_confidence) : null;

  return (
    <>
      <TableRow 
        hover 
        sx={{ 
          bgcolor: row.match_status === 'confirmed' ? 'success.50' : 
                   row.match_status === 'ignored' ? 'grey.100' : 
                   confidenceInfo ? confidenceInfo.bgColor : 'inherit',
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        {/* Checkbox selezione */}
        <TableCell padding="checkbox">
          <Checkbox 
            size="small" 
            checked={selected} 
            onChange={onSelect}
            disabled={row.match_status === 'confirmed' || row.match_status === 'ignored'}
          />
        </TableCell>

        {/* Expand */}
        <TableCell>
          {(row.assignedPayments?.length > 0 || hasMultipleSuggestions) && (
            <IconButton size="small" onClick={onExpand}>
              {expanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )}
        </TableCell>

        {/* Data */}
        <TableCell>
          <Typography variant="body2">
            {row.data_operazione ? format(new Date(row.data_operazione), 'dd/MM/yyyy') : '-'}
          </Typography>
        </TableCell>

        {/* Causale con evidenziazione */}
        <TableCell>
          <Typography 
            variant="body2" 
            sx={{ 
              maxWidth: 350, 
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.4
            }}
          >
            {bestSuggestion?.matchedFields 
              ? highlightCausale(bestSuggestion.matchingVersion === 'bank-v2' ? row.matching_text || [row.causale, row.descrizione_estesa].filter(Boolean).join('\n') : row.causale, bestSuggestion.matchedFields)
              : row.causale || 'Causale non disponibile'
            }
          </Typography>
          {row.descrizione_estesa && bestSuggestion?.matchingVersion !== 'bank-v2' && (
            <Typography variant="caption" color="text.secondary" display="block">
              {row.descrizione_estesa}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block">{row.bankStatement?.filename || statement.filename ? `Estratto: ${row.bankStatement?.filename || statement.filename}` : row.bank_statement_id ? `Estratto ${row.bank_statement_id}` : 'Movimento senza estratto collegato'}</Typography>
          <Box sx={{ display: { xs: 'block', md: 'none' } }}><MovementAssignments row={row} bestSuggestion={bestSuggestion} /></Box>
        </TableCell>

        {/* Match Info - Fattura/Cliente */}
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <MovementAssignments row={row} bestSuggestion={bestSuggestion} />
        </TableCell>

        {/* Importo */}
        <TableCell align="right">
          <Typography fontWeight="bold" color="success.main">
            {formatCurrency(row.importo)}
          </Typography>
          {parseFloat(row.importo_assegnato) > 0 && (
            <Typography variant="caption" color="text.secondary" display="block">
              Ass: {formatCurrency(row.importo_assegnato)}
            </Typography>
          )}
        </TableCell>

        {/* Confidence con progress bar */}
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          {row.match_confidence > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 50, mr: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(row.match_confidence, 100)} 
                  color={confidenceInfo?.color || 'primary'}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Typography variant="caption" fontWeight="medium">
                {Math.round(row.match_confidence)}%
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">-</Typography>
          )}
        </TableCell>

        {/* Stato */}
        <TableCell>{getStatusChip(row.match_status)}</TableCell>

        {/* Azioni */}
        <TableCell>
          {row.match_status !== 'confirmed' && row.match_status !== 'ignored' && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Assegna manualmente">
                <IconButton size="small" onClick={() => setAssignDialogOpen(true)}>
                  <InvoiceIcon />
                </IconButton>
              </Tooltip>
              {(row.assignedPayments?.some(a => a.stato === 'pending') || (bestSuggestion && !bestSuggestion.isGiaPagata && !bestSuggestion.customerMismatch && (bestSuggestion.matchingVersion !== 'bank-v2' || bestSuggestion.autoConfirmable === true))) && (
                <Tooltip title="Conferma">
                  <IconButton size="small" color="success" onClick={onConfirm}>
                    <CheckIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Ignora">
                <IconButton size="small" onClick={onIgnore}>
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {row.match_status === 'confirmed' && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Modifica assegnazione">
                <IconButton 
                  size="small" 
                  color="warning" 
                  onClick={() => {
                    if (window.confirm('Vuoi annullare l\'assegnazione attuale e modificarla?')) {
                      bankStatementsApi.editMovement(row.id)
                        .then(() => {
                          invalidateBankCaches(queryClient);
                        })
                        .catch(err => console.error('Errore modifica:', err));
                    }
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {row.match_status === 'ignored' && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Riabilita e assegna">
                <IconButton 
                  size="small" 
                  color="info" 
                  onClick={() => {
                    if (window.confirm('Vuoi riabilitare questa riga e assegnarla?')) {
                      bankStatementsApi.editMovement(row.id)
                        .then(() => {
                          invalidateBankCaches(queryClient);
                        })
                        .catch(err => console.error('Errore riabilitazione:', err));
                    }
                  }}
                >
                  <UndoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </TableCell>
      </TableRow>

      {/* Righe espanse con dettaglio assegnazioni e suggerimenti */}
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0 }}>
          <Collapse in={expanded}>
            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
              {/* Assegnazioni esistenti */}
              {row.assignedPayments?.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Assegnazioni:</Typography>
                  {row.assignedPayments.map(ap => (
                    <Paper key={ap.id} sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip 
                        icon={ap.tipo === 'fattura' ? <InvoiceIcon /> : <AccontoIcon />}
                        label={ap.tipo === 'fattura' ? `Fatt. ${formatNuf(ap.invoice?.sezionale, ap.invoice?.nuf)}` : 'Acconto'}
                        size="small"
                        color={ap.tipo === 'fattura' ? 'primary' : 'secondary'}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">
                          {formatCustomerName(ap.customer, 'N/D')}
                        </Typography>
                        {ap.invoice && (
                          <Typography variant="caption" color="text.secondary">
                            Data: {ap.invoice.f_emission ? format(new Date(ap.invoice.f_emission), 'dd/MM/yyyy') : '-'}
                            {ap.invoice.stato_pagamento === 'pagata' && (
                              <Chip label="GIÀ PAGATA" size="small" color="error" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                        )}
                      </Box>
                      <Typography fontWeight="bold" color="success.main">
                        {formatCurrency(ap.importo)}
                      </Typography>
                      <Chip 
                        label={ap.stato} 
                        size="small" 
                        color={ap.stato === 'confirmed' ? 'success' : 'default'}
                      />
                    </Paper>
                  ))}
                </>
              )}

              {/* Altri suggerimenti */}
              {hasMultipleSuggestions && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: row.assignedPayments?.length ? 2 : 0 }}>
                    Altri suggerimenti ({suggestions.length - 1}):
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Fattura</TableCell>
                          <TableCell>Cliente</TableCell>
                          <TableCell>Data</TableCell>
                          <TableCell align="right">Tot. Fattura</TableCell>
                          <TableCell align="right">Importo Sugg.</TableCell>
                          <TableCell>Confidence</TableCell>
                          <TableCell>Motivo</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {suggestions.slice(1).map((sugg, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Chip 
                                label={sugg.tipo} 
                                size="small" 
                                color={sugg.tipo === 'fattura' ? 'primary' : 'secondary'}
                              />
                            </TableCell>
                            <TableCell>
                              {sugg.invoiceNum || '-'}
                              {sugg.isGiaPagata && (
                                <Tooltip title={sugg.existingPayment 
                                  ? `Pagata il ${new Date(sugg.existingPayment.data).toLocaleDateString('it-IT')}`
                                  : ''
                                }>
                                  <Chip 
                                    label={sugg.existingPayment 
                                      ? `PAGATA ${new Date(sugg.existingPayment.data).toLocaleDateString('it-IT')}`
                                      : "GIÀ PAGATA"
                                    }
                                    size="small" 
                                    color="error" 
                                    sx={{ ml: 0.5 }} 
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>{sugg.customerName || '-'}</TableCell>
                            <TableCell>
                              {sugg.invoiceDate ? format(new Date(sugg.invoiceDate), 'dd/MM/yy') : '-'}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(sugg.invoiceTotal)}</TableCell>
                            <TableCell align="right">{formatCurrency(sugg.suggestedAmount)}</TableCell>
                            <TableCell>
                              <Chip 
                                label={`${sugg.confidence}%`} 
                                size="small"
                                color={getConfidenceInfo(sugg.confidence).color}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {sugg.reasons?.join(', ')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Motivi match */}
              {bestSuggestion?.reasons && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Motivi match:</strong> {bestSuggestion.reasons.join(' • ')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Dialog Assegnazione Manuale */}
      <AssignmentDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        row={row}
        statement={statement}
        queryClient={queryClient}
      />
    </>
  );
}

// ============================================
// DIALOG ASSEGNAZIONE MANUALE
// ============================================
function AssignmentDialog({ open, onClose, row, statement, queryClient }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [isAcconto, setIsAcconto] = useState(false);
  const [accontoAmount, setAccontoAmount] = useState('');
  const [invoiceSearchText, setInvoiceSearchText] = useState('');
  const [directInvoiceResults, setDirectInvoiceResults] = useState([]);
  const [searchMode, setSearchMode] = useState('customer'); // 'customer' o 'invoice'
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [showRemainingWarning, setShowRemainingWarning] = useState(false); // Dialog conferma rimanenza
  const [pendingAssignment, setPendingAssignment] = useState(null); // Dati assegnazione in attesa di conferma
  
  // Stato per ordinamento e filtri tabella fatture
  const [orderBy, setOrderBy] = useState('f_emission'); // colonna ordinamento - default data emissione
  const [orderDir, setOrderDir] = useState('desc'); // 'asc' o 'desc' - più recenti prima
  const [filters, setFilters] = useState({
    nuf: '',
    stato: 'all', // 'all', 'open', 'paid'
    importoMin: '',
    importoMax: ''
  });

  const importoDisponibile = parseFloat(row?.importo || 0) - parseFloat(row?.importo_assegnato || 0);

  // Parse suggestions dalla row per mostrare warning
  const suggestions = useMemo(() => {
    if (row?.suggestions_json) {
      try {
        return JSON.parse(row.suggestions_json);
      } catch { return []; }
    }
    return [];
  }, [row?.suggestions_json]);
  
  const bestSuggestion = suggestions[0] || null;
  const hasCustomerMismatch = bestSuggestion?.customerMismatch || false;
  const hasPaidInvoice = bestSuggestion?.isGiaPagata || false;

  // Carica fatture quando il dialog si apre con un cliente pre-assegnato
  React.useEffect(() => {
    if (open && row?.customer) {
      setSelectedCustomer(row.customer);
      loadCustomerInvoices(row.customer.id);
    } else if (!open) {
      // Reset quando il dialog si chiude
      setSelectedCustomer(null);
      setInvoiceOptions([]);
      setIsAcconto(false);
      setAccontoAmount('');
      setInvoiceSearchText('');
      setDirectInvoiceResults([]);
      setOrderBy('f_emission');
      setOrderDir('desc');
      setFilters({ nuf: '', stato: 'all', importoMin: '', importoMax: '' });
    }
  }, [open, row?.customer?.id]);

  const assignMutation = useMutation({
    mutationFn: (data) => bankStatementsApi.assignMovement(row.id, data),
    onSuccess: () => {
      invalidateBankCaches(queryClient);
      onClose();
    },
  });



  // Ricerca diretta fattura per numero o importo
  const handleInvoiceSearch = async (searchText) => {
    setInvoiceSearchText(searchText);
    if (searchText.length < 1) {
      setDirectInvoiceResults([]);
      return;
    }
    try {
      const res = await invoicesApi.getAll({ 
        search: searchText,
        limit: 30 
      });
      // Calcola residuo e ordina: prima insolute, poi pagate
      const results = (res.data?.data || []).map(inv => ({
        ...inv,
        saldo_residuo: parseFloat(inv.f_totale) - parseFloat(inv.f_paga_imp || 0),
        is_open: (parseFloat(inv.f_totale) - parseFloat(inv.f_paga_imp || 0)) > 0.01
      })).sort((a, b) => {
        // Prima le insolute
        if (a.is_open && !b.is_open) return -1;
        if (!a.is_open && b.is_open) return 1;
        // Poi per data decrescente
        return new Date(b.f_emission) - new Date(a.f_emission);
      });
      setDirectInvoiceResults(results);
    } catch (err) {
      console.error(err);
    }
  };

  // Seleziona fattura trovata dalla ricerca diretta
  const handleDirectInvoiceSelect = async (invoice) => {
    // Carica il cliente associato alla fattura
    if (invoice.customer_id) {
      try {
        const res = await customersApi.getById(invoice.customer_id);
        // La risposta è { success: true, data: {...} }
        const customer = res.data?.data || res.data;
        console.log('[PaymentList] Selected customer from invoice:', customer?.id, customer?.ragione_sociale);
        setSelectedCustomer(customer);
        // Carica tutte le fatture del cliente e pre-seleziona quella cercata
        await loadCustomerInvoices(customer.id, invoice.id);
        setSearchMode('customer');
        setDirectInvoiceResults([]);
        setInvoiceSearchText('');
      } catch (err) {
        console.error(err);
      }
    }
  };

  // Carica tutte le fatture del cliente (con opzione di pre-selezionare una fattura)
  const loadCustomerInvoices = async (customerId, invoiceIdToSelect = null) => {
    setLoadingInvoices(true);
    console.log('[PaymentList] Loading invoices for customer:', customerId, 'preselect invoice:', invoiceIdToSelect);
    try {
      const customerInvoices = await fetchCustomerInvoicesForAssignment(customerId);
      const invs = customerInvoices.map(inv => {
        const saldoResiduo = parseFloat(inv.f_totale) - parseFloat(inv.f_paga_imp || 0);
        // Confronto con conversione a numero per sicurezza
        const shouldSelect = invoiceIdToSelect && Number(inv.id) === Number(invoiceIdToSelect) && saldoResiduo > 0.01;
        if (shouldSelect) {
          console.log('[PaymentList] Pre-selecting invoice:', inv.id, formatNuf(inv.sezionale, inv.nuf), 'residuo:', saldoResiduo);
        }
        return {
          ...inv,
          saldo_residuo: saldoResiduo,
          selected: shouldSelect,
          importo_assegnato: shouldSelect ? Math.min(saldoResiduo, importoDisponibile) : 0,
          is_open: saldoResiduo > 0.01 // Flag per fatture aperte
        };
      });
      // Ordina: prima le insolute (aperte), poi le pagate, per data decrescente
      invs.sort((a, b) => {
        if (a.is_open && !b.is_open) return -1;
        if (!a.is_open && b.is_open) return 1;
        return new Date(b.f_emission) - new Date(a.f_emission);
      });
      console.log('[PaymentList] Loaded', invs.length, 'invoices, selected:', invs.filter(i => i.selected).length);
      setInvoiceOptions(invs);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleCustomerSelect = async (customer) => {
    setSelectedCustomer(customer);
    if (customer) {
      await loadCustomerInvoices(customer.id);
    }
  };

  const toggleInvoice = (invId) => {
    setInvoiceOptions(prev => prev.map(inv => {
      if (inv.id === invId) {
        // Permetti selezione solo di fatture aperte
        if (!inv.is_open) return inv;
        const newSelected = !inv.selected;
        return {
          ...inv,
          selected: newSelected,
          importo_assegnato: newSelected ? Math.min(inv.saldo_residuo, importoDisponibile) : 0
        };
      }
      return inv;
    }));
  };

  const handleSave = () => {
    const toAssign = [];
    
    if (isAcconto && accontoAmount) {
      toAssign.push({
        customer_id: selectedCustomer.id,
        tipo: 'acconto',
        importo: parseFloat(accontoAmount)
      });
    } else {
      invoiceOptions.filter(i => i.selected).forEach(inv => {
        toAssign.push({
          invoice_id: inv.id,
          customer_id: selectedCustomer.id,
          tipo: 'fattura',
          importo: parseFloat(inv.importo_assegnato)
        });
      });
    }

    if (toAssign.length > 0) {
      // Calcola la rimanenza
      const totAssegnato = toAssign.reduce((sum, t) => sum + t.importo, 0);
      const rimanenza = importoDisponibile - totAssegnato;
      
      // Se c'è rimanenza significativa (> 0.01€), mostra warning
      if (rimanenza > 0.01) {
        setPendingAssignment(toAssign);
        setShowRemainingWarning(true);
      } else {
        // Nessuna rimanenza, procedi direttamente
        assignMutation.mutate(toAssign);
      }
    }
  };

  // Conferma assegnazione con rimanenza
  const handleConfirmWithRemaining = () => {
    if (pendingAssignment) {
      assignMutation.mutate(pendingAssignment);
    }
    setShowRemainingWarning(false);
    setPendingAssignment(null);
  };

  // Annulla assegnazione con rimanenza
  const handleCancelRemaining = () => {
    setShowRemainingWarning(false);
    setPendingAssignment(null);
  };

  const totaleSelezionato = invoiceOptions
    .filter(i => i.selected)
    .reduce((sum, i) => sum + parseFloat(i.importo_assegnato || 0), 0);

  const openInvoicesCount = invoiceOptions.filter(i => i.is_open).length;
  const paidInvoicesCount = invoiceOptions.filter(i => !i.is_open).length;

  // Gestione ordinamento colonne
  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir(orderDir === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderDir(column === 'is_open' ? 'desc' : 'asc');
    }
  };

  // Fatture filtrate e ordinate
  const filteredInvoices = useMemo(() => {
    let result = [...invoiceOptions];
    
    // Applica filtri
    if (filters.nuf) {
      result = result.filter(inv => 
        formatNuf(inv.sezionale, inv.nuf).toLowerCase().includes(filters.nuf.toLowerCase())
      );
    }
    if (filters.stato === 'open') {
      result = result.filter(inv => inv.is_open);
    } else if (filters.stato === 'paid') {
      result = result.filter(inv => !inv.is_open);
    }
    if (filters.importoMin) {
      const min = parseFloat(filters.importoMin);
      result = result.filter(inv => inv.saldo_residuo >= min);
    }
    if (filters.importoMax) {
      const max = parseFloat(filters.importoMax);
      result = result.filter(inv => inv.saldo_residuo <= max);
    }
    
    // Applica ordinamento - prima per stato (aperte in cima), poi per data emissione (recenti prima)
    result.sort((a, b) => {
      // Prima ordina per stato: aperte prima di chiuse
      if (a.is_open !== b.is_open) {
        return a.is_open ? -1 : 1;
      }
      
      // Poi applica ordinamento secondario scelto dall'utente
      let cmp = 0;
      switch (orderBy) {
        case 'nuf':
          cmp = formatNuf(a.sezionale, a.nuf).localeCompare(formatNuf(b.sezionale, b.nuf));
          break;
        case 'f_emission':
          cmp = new Date(a.f_emission) - new Date(b.f_emission);
          break;
        case 'is_open':
          // Già ordinato sopra, usa data come secondario
          cmp = new Date(a.f_emission) - new Date(b.f_emission);
          break;
        case 'f_totale':
          cmp = parseFloat(a.f_totale) - parseFloat(b.f_totale);
          break;
        case 'saldo_residuo':
          cmp = a.saldo_residuo - b.saldo_residuo;
          break;
        default:
          // Default: ordina per data emissione (più recenti prima)
          cmp = new Date(a.f_emission) - new Date(b.f_emission);
      }
      return orderDir === 'asc' ? cmp : -cmp;
    });
    
    return result;
  }, [invoiceOptions, filters, orderBy, orderDir]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        Assegna Movimento Bancario
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Info movimento */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>Movimento da assegnare:</Typography>
          <Typography variant="body2">{row?.causale}</Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Chip label={row?.data_operazione ? format(new Date(row.data_operazione), 'dd/MM/yyyy') : '-'} size="small" />
            <Chip label={formatCurrency(row?.importo)} color="success" />
            <Chip label={`Disponibile: ${formatCurrency(importoDisponibile)}`} color="warning" />
          </Box>
        </Paper>

        {/* Alert per customer mismatch */}
        {hasCustomerMismatch && bestSuggestion && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>⚠️ Attenzione: Cliente non corrisponde!</strong><br />
            Nome nella causale: <strong>{bestSuggestion.extractedCustomerName || 'N/D'}</strong><br />
            Cliente della fattura {bestSuggestion.invoiceNum}: <strong>{bestSuggestion.customerName}</strong><br />
            Similarità: {bestSuggestion.customerSimilarity || 0}%
          </Alert>
        )}

        {/* Alert per fattura già pagata */}
        {hasPaidInvoice && bestSuggestion && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>⚠️ Attenzione: Fattura già pagata!</strong><br />
            La fattura n. {bestSuggestion.invoiceNum} risulta già saldata.
            {bestSuggestion.existingPayment && (
              <> Pagata il {new Date(bestSuggestion.existingPayment.data).toLocaleDateString('it-IT')} 
              per {formatCurrency(bestSuggestion.existingPayment.importo)}</>
            )}
          </Alert>
        )}

        {/* Toggle modalità ricerca */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Button 
            variant={searchMode === 'customer' ? 'contained' : 'outlined'} 
            size="small"
            onClick={() => setSearchMode('customer')}
          >
            Cerca Cliente
          </Button>
          <Button 
            variant={searchMode === 'invoice' ? 'contained' : 'outlined'} 
            size="small"
            onClick={() => setSearchMode('invoice')}
          >
            Cerca Fattura
          </Button>
        </Box>

        {searchMode === 'customer' ? (
          /* Selezione cliente */
          <BankCustomerSearch value={selectedCustomer} onChange={handleCustomerSelect} active={open} label="Cliente" sx={{ mb: 2 }} />
        ) : (
          /* Ricerca diretta fattura */
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Cerca Fattura"
              placeholder="Numero fattura o importo (es. 91909 o 231.46)"
              value={invoiceSearchText}
              onChange={(e) => handleInvoiceSearch(e.target.value)}
              helperText="Cerca per numero fattura, codice cliente o importo"
              sx={{ mb: 1 }}
            />
            {directInvoiceResults.length > 0 && (
              <Paper sx={{ maxHeight: 200, overflow: 'auto' }}>
                <List dense>
                  {directInvoiceResults.map(inv => {
                    const saldo = parseFloat(inv.f_totale) - parseFloat(inv.f_paga_imp || 0);
                    return (
                      <ListItem 
                        key={inv.id} 
                        button 
                        onClick={() => handleDirectInvoiceSelect(inv)}
                        sx={{ 
                          borderLeft: saldo > 0.01 ? '4px solid' : 'none',
                          borderColor: 'error.main'
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">
                                <strong>{formatNuf(inv.sezionale, inv.nuf)}</strong> - {formatCustomerName(inv.customer, 'N/D')}
                              </Typography>
                              <Typography variant="body2" color={saldo > 0.01 ? 'error' : 'success.main'}>
                                {saldo > 0.01 ? `Residuo: ${formatCurrency(saldo)}` : 'Pagata'}
                              </Typography>
                            </Box>
                          }
                          secondary={`Data: ${inv.f_emission ? format(new Date(inv.f_emission), 'dd/MM/yyyy') : '-'} - Totale: ${formatCurrency(inv.f_totale)}`}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            )}
          </Box>
        )}

        {selectedCustomer && (
          <>
            {/* Info cliente selezionato */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Cliente selezionato:</strong> {selectedCustomer.cod_cli} - {formatCustomerName(selectedCustomer, '')}
            </Alert>

            {/* Scelta: Fatture o Acconto */}
            <Box sx={{ mb: 2 }}>
              <Button 
                variant={!isAcconto ? 'contained' : 'outlined'} 
                onClick={() => setIsAcconto(false)}
                sx={{ mr: 1 }}
              >
                Assegna a Fatture
              </Button>
              <Button 
                variant={isAcconto ? 'contained' : 'outlined'} 
                onClick={() => setIsAcconto(true)}
              >
                Registra come Acconto
              </Button>
            </Box>

            {isAcconto ? (
              <TextField
                label="Importo Acconto"
                type="number"
                fullWidth
                value={accontoAmount}
                onChange={(e) => setAccontoAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                helperText={`Massimo: ${formatCurrency(importoDisponibile)}`}
              />
            ) : (
              <>
                {loadingInvoices ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* Riepilogo assegnazione - SOPRA la tabella */}
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        mb: 2, 
                        p: 2, 
                        bgcolor: totaleSelezionato > importoDisponibile ? 'error.main' : 
                                 totaleSelezionato === importoDisponibile ? 'success.main' : 'primary.main',
                        color: 'white',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>DISPONIBILE</Typography>
                          <Typography variant="h6" fontWeight="bold">{formatCurrency(importoDisponibile)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', flex: 1, borderLeft: '1px solid rgba(255,255,255,0.3)', borderRight: '1px solid rgba(255,255,255,0.3)' }}>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>ASSEGNATO</Typography>
                          <Typography variant="h6" fontWeight="bold">{formatCurrency(totaleSelezionato)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>RIMANENTE</Typography>
                          <Typography variant="h6" fontWeight="bold">{formatCurrency(importoDisponibile - totaleSelezionato)}</Typography>
                        </Box>
                      </Box>
                    </Paper>

                    <Typography variant="subtitle2" gutterBottom>
                      Fatture del cliente: {openInvoicesCount} aperte, {paidInvoicesCount} pagate
                      {filters.stato !== 'all' || filters.nuf || filters.importoMin || filters.importoMax ? (
                        <Chip 
                          label={`Filtrate: ${filteredInvoices.length}`} 
                          size="small" 
                          sx={{ ml: 1 }}
                          onDelete={() => setFilters({ nuf: '', stato: 'all', importoMin: '', importoMax: '' })}
                        />
                      ) : null}
                    </Typography>
                    
                    {/* Barra filtri */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="Filtra fattura..."
                        value={filters.nuf}
                        onChange={(e) => setFilters(f => ({ ...f, nuf: e.target.value }))}
                        sx={{ width: 130 }}
                        InputProps={{
                          startAdornment: <SearchIcon sx={{ fontSize: 16, color: 'grey.500', mr: 0.5 }} />
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={filters.stato}
                          onChange={(e) => setFilters(f => ({ ...f, stato: e.target.value }))}
                          displayEmpty
                        >
                          <MenuItem value="all">Tutte</MenuItem>
                          <MenuItem value="open">Da pagare</MenuItem>
                          <MenuItem value="paid">Pagate</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        placeholder="Min €"
                        type="number"
                        value={filters.importoMin}
                        onChange={(e) => setFilters(f => ({ ...f, importoMin: e.target.value }))}
                        sx={{ width: 80 }}
                      />
                      <TextField
                        size="small"
                        placeholder="Max €"
                        type="number"
                        value={filters.importoMax}
                        onChange={(e) => setFilters(f => ({ ...f, importoMax: e.target.value }))}
                        sx={{ width: 80 }}
                      />
                      {(filters.nuf || filters.stato !== 'all' || filters.importoMin || filters.importoMax) && (
                        <Button 
                          size="small" 
                          onClick={() => setFilters({ nuf: '', stato: 'all', importoMin: '', importoMax: '' })}
                          startIcon={<CloseIcon />}
                        >
                          Reset
                        </Button>
                      )}
                    </Box>
                    
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell sortDirection={orderBy === 'nuf' ? orderDir : false}>
                              <TableSortLabel
                                active={orderBy === 'nuf'}
                                direction={orderBy === 'nuf' ? orderDir : 'asc'}
                                onClick={() => handleSort('nuf')}
                              >
                                Fattura
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === 'f_emission' ? orderDir : false}>
                              <TableSortLabel
                                active={orderBy === 'f_emission'}
                                direction={orderBy === 'f_emission' ? orderDir : 'asc'}
                                onClick={() => handleSort('f_emission')}
                              >
                                Data
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === 'is_open' ? orderDir : false}>
                              <TableSortLabel
                                active={orderBy === 'is_open'}
                                direction={orderBy === 'is_open' ? orderDir : 'asc'}
                                onClick={() => handleSort('is_open')}
                              >
                                Stato
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sortDirection={orderBy === 'f_totale' ? orderDir : false}>
                              <TableSortLabel
                                active={orderBy === 'f_totale'}
                                direction={orderBy === 'f_totale' ? orderDir : 'asc'}
                                onClick={() => handleSort('f_totale')}
                              >
                                Totale
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sortDirection={orderBy === 'saldo_residuo' ? orderDir : false}>
                              <TableSortLabel
                                active={orderBy === 'saldo_residuo'}
                                direction={orderBy === 'saldo_residuo' ? orderDir : 'asc'}
                                onClick={() => handleSort('saldo_residuo')}
                              >
                                Residuo
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Da assegnare</TableCell>
                          </TableRow>
                        </TableHead>
                    <TableBody>
                      {filteredInvoices.map(inv => (
                        <TableRow 
                          key={inv.id} 
                          hover
                          sx={{ 
                            bgcolor: inv.is_open ? 'transparent' : 'grey.100',
                            opacity: inv.is_open ? 1 : 0.7
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox 
                              checked={inv.selected} 
                              onChange={() => toggleInvoice(inv.id)} 
                              disabled={!inv.is_open}
                            />
                          </TableCell>
                          <TableCell>{formatNuf(inv.sezionale, inv.nuf)}</TableCell>
                          <TableCell>{format(new Date(inv.f_emission), 'dd/MM/yy')}</TableCell>
                          <TableCell>
                            {inv.is_open ? (
                              <Chip 
                                label="Da pagare" 
                                size="small"
                                color="error"
                                variant="filled"
                              />
                            ) : (
                              <Tooltip title={inv.f_paga_dt ? `Pagata il ${format(new Date(inv.f_paga_dt), 'dd/MM/yyyy')}` : 'Pagata'}>
                                <Chip 
                                  label={inv.f_paga_dt ? `Pagata ${format(new Date(inv.f_paga_dt), 'dd/MM/yy')}` : 'Pagata'} 
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(inv.f_totale)}</TableCell>
                          <TableCell align="right" sx={{ color: inv.is_open ? 'error.main' : 'text.secondary' }}>
                            {formatCurrency(inv.saldo_residuo)}
                          </TableCell>
                          <TableCell align="right">
                            {inv.selected && inv.is_open && (
                              <TextField
                                size="small"
                                type="number"
                                value={inv.importo_assegnato}
                                onChange={(e) => {
                                  const val = Math.min(parseFloat(e.target.value) || 0, inv.saldo_residuo);
                                  setInvoiceOptions(prev => prev.map(i => 
                                    i.id === inv.id ? { ...i, importo_assegnato: val } : i
                                  ));
                                }}
                                sx={{ width: 100 }}
                                InputProps={{ startAdornment: '€' }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                  </>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={
            !selectedCustomer || 
            assignMutation.isPending ||
            (isAcconto ? !accontoAmount : totaleSelezionato === 0)
          }
        >
          {assignMutation.isPending ? <CircularProgress size={20} /> : 'Assegna'}
        </Button>
      </DialogActions>

      {/* Dialog conferma rimanenza */}
      <Dialog open={showRemainingWarning} onClose={handleCancelRemaining} maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          ⚠️ Attenzione: Rimanenza Positiva
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            L'importo assegnato è inferiore all'importo del bonifico.
          </Alert>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Importo bonifico:</strong> {formatCurrency(importoDisponibile)}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>Importo assegnato:</strong> {formatCurrency(pendingAssignment?.reduce((sum, t) => sum + t.importo, 0) || 0)}
            </Typography>
            <Typography variant="body1" color="warning.main" fontWeight="bold">
              <strong>Rimanenza:</strong> {formatCurrency(importoDisponibile - (pendingAssignment?.reduce((sum, t) => sum + t.importo, 0) || 0))}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Vuoi procedere comunque con l'assegnazione parziale? La rimanenza non verrà assegnata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemaining} color="inherit">
            Torna Indietro
          </Button>
          <Button 
            onClick={handleConfirmWithRemaining} 
            variant="contained" 
            color="warning"
          >
            Procedi Comunque
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

// ============================================
// TAB STORICO ESTRATTI CONTO
// ============================================
function StatementsHistoryTab({ statements, deleteStatementMutation, total, page, rowsPerPage, onPageChange, onRowsPerPageChange }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const handleDownload = async (stmt) => {
    try {
      const response = await bankStatementsApi.download(stmt.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', stmt.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Errore download:', err);
    }
  };

  const handleDelete = (stmt) => {
    if (window.confirm(`Eliminare l'estratto conto "${stmt.filename}"?`)) {
      deleteStatementMutation.mutate(stmt.id);
    }
  };

  return (
    <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File</TableCell>
              {!isMobile && <TableCell>Data Upload</TableCell>}
              {!isMobile && <TableCell>Periodo</TableCell>}
              <TableCell align="right">Righe</TableCell>
              {!isMobile && <TableCell align="right">Importo</TableCell>}
              {!isMobile && <TableCell align="right">Riconciliato</TableCell>}
              <TableCell>Stato</TableCell>
              <TableCell>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statements.map(stmt => (
              <TableRow key={stmt.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BankIcon color="primary" />
                    <Box>
                      <Typography variant="body2" noWrap sx={{ maxWidth: isMobile ? 120 : 'none' }}>{stmt.filename}</Typography>
                      {isMobile && stmt.data_import && (
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(stmt.data_import), 'dd/MM/yyyy')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                {!isMobile && <TableCell>
                  {stmt.data_import ? format(new Date(stmt.data_import), 'dd/MM/yyyy HH:mm') : '-'}
                </TableCell>}
                {!isMobile && <TableCell>
                  {stmt.data_inizio && stmt.data_fine ? (
                    `${format(new Date(stmt.data_inizio), 'dd/MM')} - ${format(new Date(stmt.data_fine), 'dd/MM/yyyy')}`
                  ) : '-'}
                </TableCell>}
                <TableCell align="right">
                  {stmt.totale_righe}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {stmt.righe_matchate} conf. / {stmt.righe_ignorate} ign.
                  </Typography>
                </TableCell>
                {!isMobile && <TableCell align="right">{formatCurrency(stmt.totale_importato)}</TableCell>}
                {!isMobile && <TableCell align="right" sx={{ color: 'success.main' }}>
                  {formatCurrency(stmt.totale_riconciliato)}
                </TableCell>}
                <TableCell>
                  <Chip 
                    label={stmt.stato} 
                    size="small"
                    color={
                      stmt.stato === 'completed' ? 'success' :
                      stmt.stato === 'in_progress' ? 'warning' : 'default'
                    }
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Scarica CSV originale">
                    <IconButton size="small" onClick={() => handleDownload(stmt)}>
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  {stmt.stato !== 'completed' && (
                    <Tooltip title="Elimina">
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleDelete(stmt)}
                        disabled={deleteStatementMutation.isPending}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {statements.length === 0 && (
              <TableRow>
                <TableCell colSpan={isMobile ? 4 : 8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Nessun estratto conto caricato</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Righe per pagina"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
      />
    </Card>
  );
}

export default PaymentList;
